import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ShoppingBag, BarChart2, Plus, Lock, LogOut, ArrowLeft,
  TrendingUp, Package, CheckCircle, Clock, XCircle, Loader2,
  Trash2, ChevronDown, Search, RefreshCw, MessageCircle, Minus
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PASS_KEY = 'rocha_admin_session';

const STATUS_LABELS = {
  approved: { label: 'Aprovado', color: 'text-green-700 bg-green-100' },
  completed: { label: 'Concluído', color: 'text-green-700 bg-green-100' },
  pending: { label: 'Pendente', color: 'text-amber-700 bg-amber-100' },
  pending_payment: { label: 'Ag. Pagamento', color: 'text-amber-700 bg-amber-100' },
  cancelled: { label: 'Cancelado', color: 'text-red-700 bg-red-100' },
  failed: { label: 'Falhou', color: 'text-red-700 bg-red-100' },
  rejected: { label: 'Rejeitado', color: 'text-red-700 bg-red-100' },
};

const PAYMENT_METHODS = [
  { value: 'pix', label: 'PIX' },
  { value: 'dinheiro', label: 'Dinheiro' },
  { value: 'cartao', label: 'Cartão' },
  { value: 'boleto', label: 'Boleto' },
  { value: 'whatsapp', label: 'WhatsApp' },
];

const fmt = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;
const fmtDate = (d) => d ? new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

// ─── Login Screen ───
const LoginScreen = ({ onLogin }) => {
  const [pwd, setPwd] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/rocha/admin/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: pwd }),
      });
      if (res.ok) {
        localStorage.setItem(PASS_KEY, pwd);
        onLogin(pwd);
      } else {
        setError('Senha incorreta');
      }
    } catch {
      setError('Erro de conexão');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a1555] flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-sm">
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-[#0a1555] rounded-2xl flex items-center justify-center mx-auto mb-3">
            <Lock className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-xl font-bold text-gray-900">Painel Rocha</h1>
          <p className="text-sm text-gray-500 mt-1">Área administrativa</p>
        </div>
        <form onSubmit={handleLogin} className="space-y-4">
          <input
            type="password"
            value={pwd}
            onChange={e => setPwd(e.target.value)}
            placeholder="Senha de acesso"
            className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1555]/20 focus:border-[#0a1555]"
            data-testid="admin-password-input"
            autoFocus
          />
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <button
            type="submit"
            disabled={loading || !pwd}
            className="w-full py-3 bg-[#0a1555] hover:bg-[#162289] text-white rounded-xl font-semibold text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="admin-login-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            Entrar
          </button>
        </form>
      </div>
    </div>
  );
};

// ─── Dashboard Tab ───
const DashboardTab = ({ password }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/rocha/admin/dashboard?password=${encodeURIComponent(password)}`)
      .then(r => r.json()).then(setData).finally(() => setLoading(false));
  }, [password]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#0a1555]" /></div>;
  if (!data) return null;

  const cards = [
    { label: 'Receita Total', value: fmt(data.total_revenue), icon: TrendingUp, color: 'bg-green-50 text-green-700' },
    { label: 'Receita do Mês', value: fmt(data.month_revenue), icon: BarChart2, color: 'bg-blue-50 text-blue-700' },
    { label: 'Receita da Semana', value: fmt(data.week_revenue), icon: TrendingUp, color: 'bg-purple-50 text-purple-700' },
    { label: 'Total de Pedidos', value: data.total_orders, icon: Package, color: 'bg-gray-50 text-gray-700' },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <div key={i} className="bg-white rounded-2xl p-5 shadow-sm">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${c.color}`}>
              <c.icon className="w-5 h-5" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{c.value}</p>
            <p className="text-xs text-gray-500 mt-1">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Status */}
        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <h3 className="font-semibold text-gray-900 mb-4">Status dos Pedidos</h3>
          <div className="space-y-3">
            {[
              { label: 'Aprovados / Concluídos', count: data.approved_orders, icon: CheckCircle, color: 'text-green-500' },
              { label: 'Pendentes', count: data.pending_orders, icon: Clock, color: 'text-amber-500' },
              { label: 'Cancelados / Falhou', count: data.cancelled_orders, icon: XCircle, color: 'text-red-500' },
            ].map((s, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                <div className="flex items-center gap-2">
                  <s.icon className={`w-4 h-4 ${s.color}`} />
                  <span className="text-sm text-gray-600">{s.label}</span>
                </div>
                <span className="font-bold text-gray-900">{s.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Top Products */}
        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <h3 className="font-semibold text-gray-900 mb-4">Produtos Mais Vendidos</h3>
          {data.top_products.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-6">Nenhuma venda registrada ainda</p>
          ) : (
            <div className="space-y-3">
              {data.top_products.map((p, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 bg-[#0a1555] text-white text-xs font-bold rounded-full flex items-center justify-center">{i + 1}</span>
                    <span className="text-sm text-gray-700 truncate max-w-[200px]">{p.name}</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900">{p.qty} un.</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Orders Tab ───
const OrdersTab = ({ password }) => {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ password });
    if (filterStatus) params.append('status', filterStatus);
    if (filterType) params.append('type', filterType);
    const res = await fetch(`${API_URL}/api/rocha/admin/orders?${params}`);
    const data = await res.json();
    setOrders(data.orders || []);
    setTotal(data.total || 0);
    setLoading(false);
  }, [password, filterStatus, filterType]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (orderId, newStatus) => {
    setUpdatingStatus(orderId);
    await fetch(`${API_URL}/api/rocha/admin/orders/${orderId}/status?password=${encodeURIComponent(password)}&new_status=${newStatus}`, { method: 'PUT' });
    await load();
    setUpdatingStatus(null);
  };

  const filtered = search
    ? orders.filter(o => o.buyer?.name?.toLowerCase().includes(search.toLowerCase()) || o.id?.includes(search))
    : orders;

  const StatusBadge = ({ status }) => {
    const s = STATUS_LABELS[status] || { label: status, color: 'text-gray-600 bg-gray-100' };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.color}`}>{s.label}</span>;
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-2xl p-4 shadow-sm flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar cliente ou ID..."
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1555]/20" />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1555]/20">
          <option value="">Todos os status</option>
          {Object.entries(STATUS_LABELS).map(([v, s]) => <option key={v} value={v}>{s.label}</option>)}
        </select>
        <select value={filterType} onChange={e => setFilterType(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1555]/20">
          <option value="">Todos os tipos</option>
          <option value="manual">Manual</option>
          <option value="mercadopago">Mercado Pago</option>
        </select>
        <button onClick={load} className="p-2 text-gray-500 hover:text-[#0a1555] transition-colors"><RefreshCw className="w-4 h-4" /></button>
        <span className="text-xs text-gray-400">{total} pedidos</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#0a1555]" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
          <Package className="w-12 h-12 text-gray-200 mx-auto mb-3" />
          <p className="text-gray-400">Nenhum pedido encontrado</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(order => (
            <div key={order.id} className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div
                className="flex items-center gap-4 p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setExpanded(expanded === order.id ? null : order.id)}
                data-testid={`order-row-${order.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900 text-sm">{order.buyer?.name || 'Cliente'}</span>
                    <StatusBadge status={order.status} />
                    {order.type === 'manual' && (
                      <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">Manual</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">{fmtDate(order.created_at)} · {order.items?.length || 0} item(s)</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="font-bold text-gray-900 text-sm">{fmt(order.total)}</p>
                  <p className="text-xs text-gray-400">{order.payment?.payment_type || '—'}</p>
                </div>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expanded === order.id ? 'rotate-180' : ''}`} />
              </div>

              {expanded === order.id && (
                <div className="border-t border-gray-100 p-4 bg-gray-50 space-y-3">
                  {/* Items */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Produtos</p>
                    <div className="space-y-1">
                      {order.items?.map((item, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-gray-700">{item.quantity}x {item.name}</span>
                          <span className="font-medium text-gray-900">{fmt(item.subtotal)}</span>
                        </div>
                      ))}
                      {order.shipping_cost > 0 && (
                        <div className="flex justify-between text-sm text-gray-500">
                          <span>Frete</span><span>{fmt(order.shipping_cost)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Contact */}
                  {order.buyer?.phone && (
                    <div className="flex items-center gap-2">
                      <a href={`https://wa.me/55${order.buyer.phone.replace(/\D/g,'')}?text=${encodeURIComponent('🟢 Olá! Vim do site, tenho interesse em comprar peças.')}`}
                        target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1.5 text-xs text-green-600 hover:text-green-800 font-medium transition-colors">
                        <MessageCircle className="w-3.5 h-3.5" />
                        {order.buyer.phone}
                      </a>
                    </div>
                  )}
                  {order.notes && <p className="text-xs text-gray-500 italic">Obs: {order.notes}</p>}
                  {/* Status update */}
                  <div className="flex items-center gap-2 pt-1">
                    <span className="text-xs text-gray-500">Alterar status:</span>
                    {['completed', 'cancelled', 'pending'].map(s => (
                      <button key={s} onClick={() => updateStatus(order.id, s)} disabled={updatingStatus === order.id || order.status === s}
                        className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors disabled:opacity-40 ${
                          s === 'completed' ? 'bg-green-100 text-green-700 hover:bg-green-200' :
                          s === 'cancelled' ? 'bg-red-100 text-red-700 hover:bg-red-200' :
                          'bg-amber-100 text-amber-700 hover:bg-amber-200'}`}>
                        {updatingStatus === order.id ? '...' : STATUS_LABELS[s]?.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── New Sale Tab ───
const NewSaleTab = ({ password, products }) => {
  const [buyerName, setBuyerName] = useState('');
  const [buyerPhone, setBuyerPhone] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('pix');
  const [shippingCost, setShippingCost] = useState('');
  const [notes, setNotes] = useState('');
  const [saleStatus, setSaleStatus] = useState('completed');
  const [items, setItems] = useState([{ product_id: '', name: '', quantity: 1, unit_price: 0 }]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const addItem = () => setItems(prev => [...prev, { product_id: '', name: '', quantity: 1, unit_price: 0 }]);
  const removeItem = (i) => setItems(prev => prev.filter((_, idx) => idx !== i));

  const selectProduct = (idx, productId) => {
    const prod = products.find(p => p.id === productId);
    if (!prod) return;
    setItems(prev => prev.map((item, i) => i === idx ? {
      ...item,
      product_id: prod.id,
      name: prod.name,
      unit_price: prod.sale_price || prod.regular_price || 0,
    } : item));
  };

  const updateItem = (idx, field, value) => {
    setItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  const subtotal = items.reduce((s, i) => s + (Number(i.unit_price) * Number(i.quantity)), 0);
  const total = subtotal + Number(shippingCost || 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!buyerName.trim()) { setError('Preencha o nome do cliente'); return; }
    if (items.some(i => !i.name.trim())) { setError('Preencha todos os produtos'); return; }
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/rocha/admin/orders/manual?password=${encodeURIComponent(password)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          buyer_name: buyerName, buyer_phone: buyerPhone.replace(/\D/g, '') || null,
          items: items.map(i => ({ ...i, quantity: Number(i.quantity), unit_price: Number(i.unit_price) })),
          payment_method: paymentMethod,
          shipping_cost: Number(shippingCost || 0),
          notes: notes || null,
          status: saleStatus,
        }),
      });
      if (!res.ok) throw new Error('Erro ao salvar');
      setSuccess(true);
      setBuyerName(''); setBuyerPhone(''); setNotes(''); setShippingCost('');
      setItems([{ product_id: '', name: '', quantity: 1, unit_price: 0 }]);
      setTimeout(() => setSuccess(false), 3000);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const formatPhone = (v) => {
    const d = v.replace(/\D/g,'').slice(0,11);
    if (d.length <= 2) return d;
    if (d.length <= 7) return `(${d.slice(0,2)}) ${d.slice(2)}`;
    return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5 max-w-2xl">
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3 text-green-700 font-medium">
          <CheckCircle className="w-5 h-5" /> Venda registrada com sucesso!
        </div>
      )}

      {/* Client */}
      <div className="bg-white rounded-2xl p-5 shadow-sm space-y-4">
        <h3 className="font-semibold text-gray-900">Cliente</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Nome *</label>
            <input value={buyerName} onChange={e => setBuyerName(e.target.value)} placeholder="Nome completo"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1555]/20" data-testid="sale-buyer-name" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Telefone</label>
            <input value={buyerPhone} onChange={e => setBuyerPhone(formatPhone(e.target.value))} placeholder="(63) 99999-9999"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1555]/20" data-testid="sale-buyer-phone" />
          </div>
        </div>
      </div>

      {/* Products */}
      <div className="bg-white rounded-2xl p-5 shadow-sm space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Produtos *</h3>
          <button type="button" onClick={addItem}
            className="flex items-center gap-1.5 text-sm text-[#0a1555] hover:text-[#162289] font-medium transition-colors">
            <Plus className="w-4 h-4" /> Adicionar
          </button>
        </div>
        {items.map((item, idx) => (
          <div key={idx} className="grid grid-cols-12 gap-2 items-start">
            <div className="col-span-12 sm:col-span-5">
              <select value={item.product_id} onChange={e => selectProduct(idx, e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1555]/20" data-testid={`sale-product-select-${idx}`}>
                <option value="">Selecionar produto...</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div className="col-span-12 sm:col-span-3">
              <input value={item.name} onChange={e => updateItem(idx, 'name', e.target.value)} placeholder="Nome do produto"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1555]/20" />
            </div>
            <div className="col-span-4 sm:col-span-1">
              <input type="number" value={item.quantity} min="1" onChange={e => updateItem(idx, 'quantity', e.target.value)}
                className="w-full px-2 py-2.5 border border-gray-200 rounded-xl text-sm text-center focus:ring-2 focus:ring-[#0a1555]/20" data-testid={`sale-qty-${idx}`} />
            </div>
            <div className="col-span-6 sm:col-span-2">
              <input type="number" step="0.01" value={item.unit_price} min="0" onChange={e => updateItem(idx, 'unit_price', e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1555]/20" placeholder="R$ 0,00" data-testid={`sale-price-${idx}`} />
            </div>
            <div className="col-span-2 sm:col-span-1 flex justify-end">
              {items.length > 1 && (
                <button type="button" onClick={() => removeItem(idx)} className="p-2.5 text-red-400 hover:text-red-600 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}
        <div className="pt-2 border-t border-gray-100 text-right text-sm font-semibold text-gray-700">
          Subtotal: {fmt(subtotal)}
        </div>
      </div>

      {/* Payment */}
      <div className="bg-white rounded-2xl p-5 shadow-sm space-y-4">
        <h3 className="font-semibold text-gray-900">Pagamento</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Forma de pagamento *</label>
            <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1555]/20" data-testid="sale-payment-method">
              {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Frete (R$)</label>
            <input type="number" step="0.01" value={shippingCost} onChange={e => setShippingCost(e.target.value)} placeholder="0,00" min="0"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1555]/20" data-testid="sale-shipping" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <select value={saleStatus} onChange={e => setSaleStatus(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1555]/20" data-testid="sale-status">
              <option value="completed">Concluído</option>
              <option value="pending">Pendente</option>
              <option value="cancelled">Cancelado</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Observações</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Informações adicionais..."
            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#0a1555]/20 resize-none" data-testid="sale-notes" />
        </div>
      </div>

      {/* Total + Submit */}
      <div className="bg-white rounded-2xl p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <span className="font-semibold text-gray-900">Total da venda</span>
          <span className="text-2xl font-bold text-[#0a1555]">{fmt(total)}</span>
        </div>
        {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
        <button type="submit" disabled={submitting}
          className="w-full py-3 bg-[#0a1555] hover:bg-[#162289] text-white rounded-xl font-semibold text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="sale-submit-btn">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Registrar Venda
        </button>
      </div>
    </form>
  );
};

// ─── Main Admin Page ───
export default function RochaAdminPage() {
  const [password, setPassword] = useState(() => localStorage.getItem(PASS_KEY) || '');
  const [authenticated, setAuthenticated] = useState(false);
  const [tab, setTab] = useState('dashboard');
  const [products, setProducts] = useState([]);

  useEffect(() => {
    if (password) {
      fetch(`${API_URL}/api/rocha/admin/auth`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      }).then(r => { if (r.ok) setAuthenticated(true); else localStorage.removeItem(PASS_KEY); });
    }
  }, [password]);

  useEffect(() => {
    if (authenticated) {
      fetch(`${API_URL}/api/products?partner_slug=rocha&limit=200`)
        .then(r => r.json()).then(setProducts).catch(() => {});
    }
  }, [authenticated]);

  const logout = () => { localStorage.removeItem(PASS_KEY); setAuthenticated(false); setPassword(''); };

  if (!authenticated) {
    return <LoginScreen onLogin={(pwd) => { setPassword(pwd); setAuthenticated(true); }} />;
  }

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart2 },
    { id: 'orders', label: 'Pedidos', icon: ShoppingBag },
    { id: 'new', label: 'Registrar Venda', icon: Plus },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#0a1555] px-6 py-3">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/parceiros/rocha" className="text-white/60 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-white font-bold text-base">Painel Rocha</h1>
          </div>
          <button onClick={logout} className="flex items-center gap-1.5 text-white/60 hover:text-white text-sm transition-colors">
            <LogOut className="w-4 h-4" /> Sair
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 px-6">
        <div className="max-w-5xl mx-auto flex gap-1">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-3.5 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id ? 'border-[#0a1555] text-[#0a1555]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              data-testid={`tab-${t.id}`}>
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-6">
        {tab === 'dashboard' && <DashboardTab password={password} />}
        {tab === 'orders' && <OrdersTab password={password} />}
        {tab === 'new' && <NewSaleTab password={password} products={products} />}
      </div>
    </div>
  );
}
